import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search,
  Clock,
  Trash2,
  RotateCcw,
  MoreHorizontal,
  Sparkles,
  Type,
  Image,
  SlidersHorizontal,
  Filter,
  MousePointerClick,
  FileText,
  TrendingUp,
  Calendar,
  ExternalLink,
} from 'lucide-react';
import { researchService } from '@/services/api/research';
import type { SearchHistoryItem } from '@/types/research';
import { cn } from '@/lib/utils';

const QUERY_TYPE_CONFIG: Record<SearchHistoryItem['queryType'], { icon: React.ReactNode; color: string; label: string }> = {
  semantic: { icon: <Sparkles className="h-4 w-4" />, color: 'bg-purple-500', label: 'Semantic' },
  keyword: { icon: <Type className="h-4 w-4" />, color: 'bg-blue-500', label: 'Keyword' },
  image: { icon: <Image className="h-4 w-4" />, color: 'bg-pink-500', label: 'Image' },
  advanced: { icon: <SlidersHorizontal className="h-4 w-4" />, color: 'bg-orange-500', label: 'Advanced' },
};

export function ResearchSearchHistoryPage() {
  const [searchFilter, setSearchFilter] = useState('');
  const [showClearDialog, setShowClearDialog] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['research-search-history'],
    queryFn: () => researchService.listSearchHistory(),
  });

  const searchHistory = data?.data || [];

  const filteredHistory = searchHistory.filter((item) =>
    item.query.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return 'Yesterday';

    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const groupByDate = (items: SearchHistoryItem[]) => {
    const groups: Record<string, SearchHistoryItem[]> = {};
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    items.forEach((item) => {
      const date = new Date(item.searchedAt);
      let key: string;

      if (date.toDateString() === today.toDateString()) {
        key = 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
        key = 'Yesterday';
      } else if (date > new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)) {
        key = 'This Week';
      } else {
        key = 'Earlier';
      }

      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });

    return groups;
  };

  const groupedHistory = groupByDate(filteredHistory);

  // Calculate stats
  const totalSearches = searchHistory.length;
  const semanticSearches = searchHistory.filter((s) => s.queryType === 'semantic').length;
  const avgResults = Math.round(
    searchHistory.reduce((acc, s) => acc + s.resultCount, 0) / (totalSearches || 1)
  );
  const totalClicks = searchHistory.reduce((acc, s) => acc + s.clickedResourceIds.length, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Search History</h1>
          <p className="text-muted-foreground">
            View and manage your past searches across the research platform
          </p>
        </div>
        <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="text-destructive hover:text-destructive">
              <Trash2 className="mr-2 h-4 w-4" />
              Clear All History
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Clear Search History?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete all your search history. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Clear All
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Searches</p>
                <p className="text-2xl font-bold">{totalSearches}</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
                <Search className="h-5 w-5 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Semantic Searches</p>
                <p className="text-2xl font-bold">{semanticSearches}</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10">
                <Sparkles className="h-5 w-5 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Results</p>
                <p className="text-2xl font-bold">{avgResults}</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
                <FileText className="h-5 w-5 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Resources Clicked</p>
                <p className="text-2xl font-bold">{totalClicks}</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10">
                <MousePointerClick className="h-5 w-5 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Filter search history..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Search History List */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="flex-1">
                    <Skeleton className="h-5 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredHistory.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="pt-12 pb-12 text-center">
            <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No search history</h3>
            <p className="text-muted-foreground">
              {searchFilter
                ? 'No searches match your filter'
                : 'Your search history will appear here'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedHistory).map(([group, items]) => (
            <div key={group}>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {group}
              </h3>
              <div className="space-y-2">
                {items.map((item) => {
                  const typeConfig = QUERY_TYPE_CONFIG[item.queryType];

                  return (
                    <Card key={item.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className={cn(
                            'flex h-10 w-10 items-center justify-center rounded-lg text-white flex-shrink-0',
                            typeConfig.color
                          )}>
                            {typeConfig.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{item.query}</p>
                                <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <FileText className="h-3 w-3" />
                                    {item.resultCount} results
                                  </span>
                                  {item.clickedResourceIds.length > 0 && (
                                    <span className="flex items-center gap-1">
                                      <MousePointerClick className="h-3 w-3" />
                                      {item.clickedResourceIds.length} clicked
                                    </span>
                                  )}
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {formatDate(item.searchedAt)}
                                  </span>
                                </div>
                                {/* Filters */}
                                {item.filters && Object.keys(item.filters).length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {Object.entries(item.filters).map(([key, value]) => (
                                      <Badge key={key} variant="secondary" className="text-xs">
                                        <Filter className="mr-1 h-2.5 w-2.5" />
                                        {key}: {String(value)}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <Badge variant="outline" className="text-xs">
                                  {typeConfig.label}
                                </Badge>
                                <Button variant="outline" size="sm">
                                  <RotateCcw className="mr-2 h-3 w-3" />
                                  Re-run
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem>
                                      <RotateCcw className="mr-2 h-4 w-4" />
                                      Run Again
                                    </DropdownMenuItem>
                                    <DropdownMenuItem>
                                      <ExternalLink className="mr-2 h-4 w-4" />
                                      View Results
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="text-destructive">
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tip Card */}
      <Card className="bg-gradient-to-r from-primary/5 to-purple-500/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h4 className="font-semibold">Pro Tip: Use Semantic Search</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Semantic search understands the meaning of your query, not just keywords. Try asking questions
                like "What datasets analyze coral bleaching patterns?" for more relevant results.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
